import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Lock } from 'lucide-react';
import { Piko } from '../components/Piko';
import { audio } from '../core/audio';
import { getState } from '../core/storage';
import { lang, t } from '../core/i18n';
import { WORLDS, GAMES, gameName, getGame } from '../content/games';
import { STICKERS, DAILY_STICKERS } from '../content/collection';
import { useEntitlement } from '../core/entitlement';

export default function StickerBook() {
  const nav = useNavigate();
  const { entitlement } = useEntitlement();
  const st = getState();
  const en = lang() === 'en';
  const isPro = entitlement?.plan === 'PRO';

  useEffect(() => { audio.init(); }, []);

  const byWorld = Object.keys(WORLDS).map((w) => ({
    world: WORLDS[w],
    games: GAMES.filter((g) => g.world === w),
  }));

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ background: 'linear-gradient(180deg,#FCF9F2,#FFE9C7)' }} data-testid="sticker-book">
      <div className="max-w-md mx-auto px-5 py-6">
        <div className="flex items-center justify-between">
          <button data-testid="stickers-home-btn" onClick={() => nav('/home')}
            className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow">
            <Home size={26} strokeWidth={3} className="text-navy/70" />
          </button>
          <Piko pose="happy" size={72} />
        </div>
        <h1 className="font-heading text-3xl font-bold text-navy mt-3">{en ? "Piko's Sticker Book" : 'Buku Pelekat Piko'}</h1>
        <p className="font-body text-navy/60 text-sm mt-1">{en ? 'Complete a game to earn its sticker' : 'Lengkapkan permainan untuk dapat pelekatnya'}</p>

        {/* Daily return stickers */}
        <div className="mt-6" data-testid="sticker-section-daily">
          <h2 className="font-heading text-lg font-bold text-navy">{en ? 'Daily Gifts' : 'Hadiah Harian'}</h2>
          <p className="font-body text-navy/50 text-xs mt-0.5">{en ? 'Come back every day for a new sticker!' : 'Kembali setiap hari untuk pelekat baharu!'}</p>
          <div className="grid grid-cols-4 gap-2.5 mt-3">
            {DAILY_STICKERS.map((s, i) => {
              const earned = st.collection.stickers.includes(s.id);
              return (
                <motion.div key={s.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.03 * i }}
                  className="flex flex-col items-center gap-1 bg-white/90 rounded-[1.3rem] p-2.5 shadow-sm min-h-[84px] justify-center"
                  style={{ border: earned ? '3px solid #FFD166' : '3px solid #ffffff' }}
                  data-testid={`daily-sticker-${s.id}`}>
                  <span className="text-3xl" style={{ filter: earned ? 'none' : 'grayscale(1) opacity(0.25)' }}>{earned ? s.emoji : '❔'}</span>
                  {earned && <p className="font-body text-[9px] font-bold text-navy/70 text-center leading-tight">{s.name[en ? 1 : 0]}</p>}
                </motion.div>
              );
            })}
          </div>
        </div>

        {byWorld.map(({ world, games }) => (
          <div key={world.id} className="mt-6" data-testid={`sticker-section-${world.id}`}>
            <h2 className="font-heading text-lg font-bold text-navy">{t(world.nameKey)}</h2>
            <div className="grid grid-cols-3 gap-3 mt-3">
              {games.map((g, i) => {
                const sticker = STICKERS[g.id];
                const earned = st.collection.stickers.includes(g.id);
                const gated = !g.free && !isPro;
                return (
                  <motion.div key={g.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.05 * i }}
                    className="relative flex flex-col items-center gap-1.5 bg-white/90 rounded-[1.6rem] p-3 shadow-sm min-h-[110px] justify-center"
                    style={{ border: earned ? '4px solid #FFD166' : '4px solid #ffffff' }}
                    data-testid={`sticker-${g.id}`}>
                    <span className="text-4xl" style={{ filter: earned ? 'none' : 'grayscale(1) opacity(0.3)' }}>{sticker.emoji}</span>
                    <p className="font-body text-[11px] font-bold text-navy/70 text-center leading-tight">
                      {earned ? sticker.name[en ? 1 : 0] : gameName(g, en)}
                    </p>
                    {gated && !earned && (
                      <span className="absolute top-1.5 right-1.5 flex items-center gap-0.5 bg-navy text-sunshine font-heading font-bold text-[9px] px-1.5 py-0.5 rounded-full"><Lock size={8} />PRO</span>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
